/**
 * TutorDrawer — "chat sul blocco" side panel. Slides in over the reader's
 * right edge (the reader column itself never resizes/pushes) and always
 * starts from a block's "Chat" action carrying a `focusBlock`. Single
 * conversation, no sidebar/list — reuses `useTutorConversation` +
 * `TutorConversationView` exactly like TutorOverlay's active-conversation
 * column does, just without the conversations list next to it.
 *
 * Lifecycle (owned by Reader.tsx's `useTutorDrawerState`, see that file):
 *   - `minimized`: this component stays MOUNTED — its `useTutorConversation`
 *     instance (messages, draft, streaming) must survive minimizing, so the
 *     panel is visually collapsed via a WIDTH-COLLAPSING wrapper, never
 *     unmounted and never CSS `transform`ed. `transform` is deliberately
 *     avoided on the sliding wrapper: a transformed ancestor becomes the
 *     containing block for any `position: fixed` descendant, and
 *     TutorCitationDialog (mounted inside TutorConversationView) is exactly
 *     that — a fixed, viewport-covering modal. Transforming this drawer
 *     would shrink that modal's "cover the whole viewport" positioning down
 *     to the drawer's own small box. Width-collapsing avoids that entirely
 *     (no transform anywhere in this component), and — combined with
 *     `overflow: hidden` and a width capped at `min(400px, 100vw)` at every
 *     point in the animation — can never contribute horizontal overflow to
 *     the page (Tailwind v4 preflight footgun, see project convention).
 *   - A fresh `key` from `useTutorDrawerState` remounts this component (and
 *     therefore a brand-new `useTutorConversation`) whenever a NEW
 *     chat-from-block starts, per the "one drawer, replaced not stacked"
 *     contract — nothing in this file itself needs to know about that; it
 *     just always initializes fresh (`initialConversationId: null`) because
 *     a TutorDrawer instance's whole lifetime IS one chat-from-block.
 */

import * as React from 'react'
import { useEffect, useState } from 'react'
import Text from '@arianna/design-system/Text'
import { fetchTutorBlockContent, type TutorBlockContent } from '../tutorApi.js'
import { useTutorConversation } from './useTutorConversation.js'
import { TutorConversationView } from './TutorConversationView.js'
import { TutorInlineMarkdown } from './TutorMarkdown.js'
import type { TutorOverlayFocusBlock } from './tutorViewModel.js'
import type { TutorNavTarget } from '../tutorNav.js'

export interface TutorDrawerProps {
  manualVersionId: number
  focusBlock: TutorOverlayFocusBlock
  minimized: boolean
  onMinimize: () => void
  onRestore: () => void
  resolveSectionTarget: (sectionId: string, fineId?: string | null) => TutorNavTarget | null
  onNavigateToSection: (slug: string, anchor: string) => void
  onLaunchQuiz: (quizSessionId: string) => void
}

const DRAWER_WIDTH = 'min(400px, 100vw)'

export function TutorDrawer({
  manualVersionId,
  focusBlock,
  minimized,
  onMinimize,
  onRestore,
  resolveSectionTarget,
  onNavigateToSection,
  onLaunchQuiz,
}: TutorDrawerProps) {
  const conversation = useTutorConversation({
    manualVersionId,
    initialConversationId: null,
    focusBlock,
    resolveSectionTarget,
    onNavigateToSection,
    onLaunchQuiz,
  })

  const isEmpty = !conversation.messagesLoading && conversation.messages.length === 0

  // Short recap of the canonical block this chat started from — reuses the
  // same read-only endpoint (Tutor::BlockContentsController) the citation
  // popup already fetches from (see TutorCitationDialog.tsx), so no backend
  // change is needed. Purely a "reminder of what you're chatting about"
  // affordance: best-effort (silently absent on failure), never blocks the
  // conversation itself.
  const [blockRecap, setBlockRecap] = useState<TutorBlockContent | null>(null)
  useEffect(() => {
    let cancelled = false
    void fetchTutorBlockContent(manualVersionId, focusBlock.blockId)
      .then((block) => {
        if (!cancelled) setBlockRecap(block)
      })
      .catch(() => {
        // Best-effort only — the chat works fine without the recap.
      })
    return () => {
      cancelled = true
    }
  }, [manualVersionId, focusBlock.blockId])

  return (
    <>
      {minimized && (
        <button
          onClick={onRestore}
          title="Riapri la chat sul blocco"
          aria-label="Riapri la chat sul blocco"
          className="fixed z-[900] flex items-center gap-2 rounded-l-md border border-r-0 border-line bg-surface px-3 py-2.5 shadow-lg cursor-pointer hover:bg-surface-2 transition-colors"
          style={{ top: '50%', right: 0, transform: 'translateY(-50%)' }}
        >
          <span aria-hidden="true" className="text-accent">
            💬
          </span>
          <Text variant="meta" className="text-ink">
            Tutor
          </Text>
        </button>
      )}

      {/*
        Outer "slot": always anchored at the viewport's right edge, width
        animates between 0 and DRAWER_WIDTH (never more), `overflow: hidden`
        clips its child during the transition. Because the slot's own width
        never exceeds DRAWER_WIDTH (<= 100vw), it can never cause horizontal
        page overflow, at any point in the animation, minimized or not.
      */}
      <div
        aria-hidden={minimized}
        className="fixed top-0 right-0 bottom-0 z-[950] overflow-hidden"
        style={{
          width: minimized ? '0px' : DRAWER_WIDTH,
          transition: 'width 200ms ease',
        }}
      >
        {/*
          Inner panel: FIXED width regardless of the slot's current width, so
          the composer/messages never reflow/squash mid-animation — the slot
          simply clips whatever doesn't fit.
        */}
        <div
          className="h-full flex flex-col bg-bg border-l border-line shadow-2xl"
          style={{ width: DRAWER_WIDTH }}
        >
          <div className="flex items-center gap-3 px-4 py-3 border-b border-line shrink-0">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] text-ink-3 font-sans font-semibold uppercase tracking-[0.12em]">
                Tutor
              </div>
              <Text variant="ui" className="truncate text-ink">
                Chat sul blocco
              </Text>
            </div>
            <button
              onClick={onMinimize}
              title="Minimizza"
              aria-label="Minimizza"
              className="inline-flex items-center justify-center size-7 rounded text-ink-4 hover:text-ink bg-transparent border-none cursor-pointer shrink-0"
            >
              ×
            </button>
          </div>

          {blockRecap != null && (
            <div className="px-4 py-3 border-b border-line shrink-0 bg-surface">
              <div className="text-[10px] text-ink-3 font-sans font-semibold uppercase tracking-[0.12em] mb-1">
                {blockRecap.sub_para_id}
                {blockRecap.title ? ` — ${blockRecap.title}` : ''}
              </div>
              <div className="font-serif italic text-[13px] leading-snug text-ink-2 line-clamp-2">
                <TutorInlineMarkdown content={blockRecap.text} />
              </div>
            </div>
          )}

          <TutorConversationView
            emptyStateMessage="Su questo pezzo chiedimi tutto — anche quello che ti sembra ovvio."
            isEmpty={isEmpty}
            compact
            {...conversation}
          />
        </div>
      </div>
    </>
  )
}
